import React, { useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X, ListMagnifyingGlass, CheckCircle, CaretDown } from "@phosphor-icons/react";
import {
  BODY_SYSTEMS,
  SYMPTOM_CATALOG,
  findSymptomMatches,
  getSymptomByCode,
  getBodySystemLabel,
  getSymptomsBySystem,
} from "@/lib/symptomCatalog";

/**
 * VA-aligned symptom picker.
 *
 * Two paths to log a symptom:
 *  1. Fast path: combobox with live search + keyboard nav.
 *  2. Browse-all: full-screen modal grouped by body system.
 *
 * The picked symptom updates `value` (a {code, label, bodySystem, bodyRegion}
 * shape) and also returns the canonical region so the host can pre-fill its
 * own body-regions field.
 *
 * The user can also free-type — we don't lock them into the taxonomy. Hitting
 * Escape collapses the dropdown and lets the typed text stand as the label.
 *
 * Props:
 *   value:        { code, label, bodySystem, bodyRegion } | null
 *   onChange:     (next: { code, label, bodySystem, bodyRegion }) => void
 *   onFreeText:   (label: string) => void — called when the user keeps a
 *                 manually-typed symptom that doesn't match the catalog.
 */
export default function SymptomPicker({ value, onChange, onFreeText }) {
  const [open, setOpen] = useState(false);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseSystem, setBrowseSystem] = useState(BODY_SYSTEMS[0].id);
  const [draft, setDraft] = useState(value?.label || "");
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);

  // Keep local draft in sync if parent value changes externally (e.g., reset on save).
  React.useEffect(() => { setDraft(value?.label || ""); }, [value?.label, value?.code]);

  const matches = useMemo(() => findSymptomMatches(draft, 12), [draft]);

  function pick(item) {
    onChange({
      code: item.code,
      label: item.label,
      bodySystem: item.bodySystem,
      bodyRegion: item.bodyRegion || "",
    });
    setDraft(item.label);
    setOpen(false);
    setActive(0);
  }

  function handleKey(e) {
    if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive((a) => (a + 1) % Math.max(matches.length, 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a - 1 + matches.length) % Math.max(matches.length, 1)); }
    else if (e.key === "Enter" && open && matches.length > 0) { e.preventDefault(); pick(matches[active]); }
    else if (e.key === "Escape") { setOpen(false); }
  }

  function handleBlur() {
    // After the dropdown closes, if the user typed something that doesn't match
    // any catalog entry, surface it as free text so the form still saves it.
    setTimeout(() => {
      setOpen(false);
      const trimmed = draft.trim();
      if (!trimmed) return;
      const found = matches.find((m) => m.label.toLowerCase() === trimmed.toLowerCase());
      if (found) { pick(found); return; }
      // Only emit onFreeText if it's actually different from the canonical label.
      if (trimmed !== (value?.label || "")) onFreeText && onFreeText(trimmed);
    }, 160);
  }

  const systemMeta = value?.bodySystem ? getBodySystemLabel(value.bodySystem) : null;

  return (
    <>
      <div style={{ position: "relative" }} data-testid="symptom-picker">
        <div style={{ display: "flex", gap: 6 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <input
              ref={inputRef}
              className="field-input"
              value={draft}
              onChange={(e) => { setDraft(e.target.value); setOpen(true); setActive(0); }}
              onFocus={() => setOpen(true)}
              onBlur={handleBlur}
              onKeyDown={handleKey}
              placeholder="Start typing a symptom (e.g. radiculopathy, hypervigilance)"
              data-testid="symptom-name"
              autoComplete="off"
              spellCheck="false"
              style={{ paddingRight: 28 }}
            />
            <CaretDown
              size={12}
              weight="bold"
              color="var(--text-tertiary)"
              style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }}
            />
            {open && matches.length > 0 && (
              <ul
                role="listbox"
                data-testid="symptom-suggestions"
                style={{
                  position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
                  marginTop: 4, padding: 4,
                  background: "var(--surface)",
                  border: "1px solid var(--border-hairline)",
                  borderRadius: "var(--radius-md)",
                  boxShadow: "0 10px 28px rgba(15,23,42,0.12)",
                  listStyle: "none",
                  maxHeight: 320,
                  overflowY: "auto",
                }}
              >
                {matches.map((m, idx) => (
                  <li
                    key={m.code}
                    role="option"
                    aria-selected={idx === active}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pick(m)}
                    onMouseEnter={() => setActive(idx)}
                    data-testid={`symptom-suggestion-${m.code}`}
                    style={{
                      padding: "7px 10px",
                      borderRadius: "var(--radius-sm)",
                      cursor: "pointer",
                      display: "flex", gap: 8, alignItems: "baseline",
                      background: idx === active ? "var(--surface-2)" : "transparent",
                    }}
                  >
                    <span style={{ fontWeight: 600, fontSize: 13, color: "var(--text-primary)", flex: 1, minWidth: 0 }}>{m.label}</span>
                    <span
                      className="mono"
                      style={{
                        fontSize: 10, fontWeight: 600,
                        color: "var(--text-secondary)",
                        background: "var(--surface-2)",
                        padding: "1px 6px", borderRadius: "var(--radius-pill)",
                        flexShrink: 0,
                      }}
                    >
                      {getBodySystemLabel(m.bodySystem)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="btn btn--outline btn--sm"
            onClick={() => setBrowseOpen(true)}
            data-testid="symptom-browse-btn"
            title="Browse all symptoms by body system"
            style={{ flexShrink: 0 }}
          >
            <ListMagnifyingGlass size={13} weight="bold" />
            <span>Browse</span>
          </button>
        </div>
        {systemMeta && value?.code && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--text-secondary)" }} data-testid="symptom-meta">
            <CheckCircle size={11} weight="fill" color="#34C759" style={{ verticalAlign: "-1px", marginRight: 4 }} />
            VA C&P term · {systemMeta}{value.bodyRegion ? ` · ${value.bodyRegion}` : ""}
          </div>
        )}
      </div>

      {browseOpen && (
        <BrowseAllModal
          activeSystem={browseSystem}
          setActiveSystem={setBrowseSystem}
          onPick={(item) => { pick(item); setBrowseOpen(false); }}
          onClose={() => setBrowseOpen(false)}
          activeCode={value?.code || ""}
        />
      )}
    </>
  );
}

function BrowseAllModal({ activeSystem, setActiveSystem, onPick, onClose, activeCode }) {
  const [q, setQ] = useState("");
  const items = useMemo(() => {
    const list = getSymptomsBySystem(activeSystem);
    if (!q.trim()) return list;
    const needle = q.trim().toLowerCase();
    return list.filter((s) => s.label.toLowerCase().includes(needle));
  }, [activeSystem, q]);

  return (
    <div className="modal-backdrop" data-testid="symptom-browse-modal" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-panel modal-panel--lg" style={{ maxWidth: 820 }}>
        <div className="modal-header">
          <ListMagnifyingGlass size={16} weight="bold" />
          <span>Browse symptoms by body system</span>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close" data-testid="symptom-browse-close">
            <X size={16} weight="bold" />
          </button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "200px 1fr", minHeight: 380 }} className="symptom-browse-grid">
          <aside
            style={{
              borderRight: "1px solid var(--border)",
              background: "var(--surface-2)",
              padding: 8,
              overflowY: "auto",
              maxHeight: "70vh",
            }}
            data-testid="symptom-browse-systems"
          >
            {BODY_SYSTEMS.map((sys) => {
              const count = getSymptomsBySystem(sys.id).length;
              const isActive = activeSystem === sys.id;
              return (
                <button
                  key={sys.id}
                  type="button"
                  onClick={() => setActiveSystem(sys.id)}
                  data-testid={`symptom-system-${sys.id}`}
                  style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid transparent",
                    background: isActive ? "var(--surface)" : "transparent",
                    borderColor: isActive ? "var(--border-hairline)" : "transparent",
                    borderRadius: "var(--radius-sm)",
                    cursor: "pointer",
                    font: "inherit", color: "inherit",
                    fontSize: 13, fontWeight: isActive ? 600 : 500,
                    marginBottom: 2,
                    textAlign: "left",
                  }}
                >
                  <span>{sys.label}</span>
                  <span className="mono" style={{ fontSize: 10, color: "var(--text-secondary)" }}>{count}</span>
                </button>
              );
            })}
          </aside>
          <section style={{ padding: 14, display: "flex", flexDirection: "column", maxHeight: "70vh" }}>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <MagnifyingGlass size={14} weight="bold" color="var(--text-tertiary)" style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
              <input
                className="field-input"
                style={{ paddingLeft: 30 }}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={`Search within ${getBodySystemLabel(activeSystem)}…`}
                data-testid="symptom-browse-search"
                autoFocus
              />
            </div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0, overflowY: "auto", display: "flex", flexDirection: "column", gap: 4 }}>
              {items.map((it) => {
                const isActive = it.code === activeCode;
                return (
                  <li key={it.code}>
                    <button
                      type="button"
                      onClick={() => onPick(it)}
                      data-testid={`symptom-browse-pick-${it.code}`}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        width: "100%", padding: "9px 12px",
                        border: `1px solid ${isActive ? "var(--branch-accent)" : "var(--border-hairline)"}`,
                        borderRadius: "var(--radius-sm)",
                        background: isActive ? "color-mix(in srgb, var(--branch-accent) 6%, var(--surface))" : "var(--surface)",
                        cursor: "pointer",
                        font: "inherit", color: "inherit",
                        textAlign: "left",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-primary)" }}>{it.label}</div>
                        {it.bodyRegion && (
                          <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginTop: 2 }}>
                            Body region: {it.bodyRegion}
                          </div>
                        )}
                      </div>
                      {isActive && <CheckCircle size={14} weight="fill" color="var(--branch-accent)" />}
                    </button>
                  </li>
                );
              })}
              {items.length === 0 && (
                <li style={{ padding: 14, color: "var(--text-secondary)", fontSize: 13, fontStyle: "italic" }}>
                  No symptoms in {getBodySystemLabel(activeSystem)} match "{q}".
                </li>
              )}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
